/**
 * Bridge between main thread and visibility Web Worker.
 * Manages worker lifecycle, request/response correlation, and fallback.
 */

import type { Wall, Point } from '@roll1/shared';
import { computeVisibilityPolygon } from './visibility';
import { clientLog } from './client-logger';

// --- Types ---

interface PendingRequest {
  resolve: (polygons: Point[][]) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface WorkerResponse {
  type: 'result' | 'error' | 'ready';
  requestId?: number;
  polygons?: Point[][];
  message?: string;
}

// --- Bridge ---

/** Timeout for worker response (ms). Falls back to main thread if exceeded. */
const WORKER_TIMEOUT_MS = 200;

export class VisibilityWorkerBridge {
  private worker: Worker | null = null;
  private available = true;
  private ready = false;
  private nextRequestId = 0;
  private pending = new Map<number, PendingRequest>();
  private fallbackWarned = false;

  constructor() {
    this.tryCreateWorker();
  }

  private tryCreateWorker(): void {
    try {
      if (typeof Worker === 'undefined') {
        this.available = false;
        return;
      }

      this.worker = new Worker(
        new URL('../workers/visibility-worker.ts', import.meta.url),
        { type: 'module' },
      );

      this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const msg = event.data;

        if (msg.type === 'ready') {
          this.ready = true;
          return;
        }

        if (msg.requestId === undefined) return;

        const pending = this.pending.get(msg.requestId);
        if (!pending) return;

        clearTimeout(pending.timeout);
        this.pending.delete(msg.requestId);

        if (msg.type === 'result' && msg.polygons) {
          pending.resolve(msg.polygons);
        } else if (msg.type === 'error') {
          pending.reject(new Error(msg.message ?? 'Worker error'));
        }
      };

      this.worker.onerror = () => {
        this.available = false;
        // Reject all pending requests
        for (const [, req] of this.pending) {
          clearTimeout(req.timeout);
          req.reject(new Error('Worker error'));
        }
        this.pending.clear();
        this.terminateWorker();
      };
    } catch {
      this.available = false;
    }
  }

  private terminateWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.ready = false;
  }

  /**
   * Compute visibility polygons for multiple origins.
   * Uses Web Worker if available, falls back to main thread otherwise.
   *
   * @param origins - Token positions
   * @param walls - Pre-filtered walls (blocking only)
   * @param ranges - Max range per origin (0 = unlimited)
   * @returns Promise resolving to array of polygons (one per origin)
   */
  async computeBatch(
    origins: Point[],
    walls: Wall[],
    ranges: number[],
  ): Promise<Point[][]> {
    // Fallback to main thread if worker unavailable
    if (!this.available || !this.worker || !this.ready) {
      return this.computeMainThread(origins, walls, ranges);
    }

    const requestId = this.nextRequestId++;

    return new Promise<Point[][]>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(requestId);
        // Worker too slow — fall back to main thread for this request
        // and mark as unavailable for future requests
        this.available = false;
        this.terminateWorker();
        if (!this.fallbackWarned) {
          this.fallbackWarned = true;
          clientLog.warn('visibility-worker', 'Worker timeout, falling back to main thread');
        }
        resolve(this.computeMainThread(origins, walls, ranges));
      }, WORKER_TIMEOUT_MS);

      this.pending.set(requestId, { resolve, reject, timeout });

      this.worker!.postMessage({
        type: 'compute',
        requestId,
        origins,
        walls,
        ranges,
        filterBlocking: false, // walls already pre-filtered
      });
    });
  }

  /**
   * Main-thread fallback computation. Produces identical results.
   */
  private computeMainThread(
    origins: Point[],
    walls: Wall[],
    ranges: number[],
  ): Point[][] {
    if (!this.fallbackWarned) {
      this.fallbackWarned = true;
      clientLog.warn('visibility-worker', 'Worker unavailable, using main thread fallback');
    }

    const polygons: Point[][] = [];
    for (let i = 0; i < origins.length; i++) {
      polygons.push(computeVisibilityPolygon(origins[i], walls, ranges[i]));
    }
    return polygons;
  }

  /** Check if worker is available */
  isAvailable(): boolean {
    return this.available && this.worker !== null && this.ready;
  }

  /** Terminate worker and clean up */
  dispose(): void {
    for (const [, req] of this.pending) {
      clearTimeout(req.timeout);
    }
    this.pending.clear();
    this.terminateWorker();
    this.available = false;
  }
}
